import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { type WebhookListParams, webhookApi } from '@/services/webhook.service'

/**
 * React-query hooks for the SUPER_ADMIN webhook ledger.
 *
 * Deliberately short `staleTime`: this is a diagnostics surface answering "what
 * just happened", so a stale page is worse than an extra fetch.
 */

const WEBHOOKS_KEY = ['admin-webhooks'] as const

export const useWebhooks = (params: WebhookListParams) =>
	useQuery({
		// The params are part of the key so paging and filtering each get their own
		// cache entry, and going back a page is instant rather than a refetch.
		queryKey: [...WEBHOOKS_KEY, params],
		queryFn: () => webhookApi.listWebhooks(params),
		staleTime: 10_000,
	})

/**
 * Re-queue a failed delivery.
 *
 * No optimistic patch here, unlike the user mutations: a retry does not change the
 * row, it asks a worker to re-process it. The new status arrives from the server
 * moments later, so predicting one would only be a guess that flickers.
 */
export const useRetryWebhook = () => {
	const queryClient = useQueryClient()
	return useMutation({
		mutationFn: (webhookId: string) => webhookApi.retryWebhook(webhookId),
		onSettled: () => queryClient.invalidateQueries({ queryKey: WEBHOOKS_KEY }),
	})
}
